import { useCallback, useEffect, useRef, useState } from "react";
import { Common } from "../../common/src";
import { userSettingAPIs, updateProjectListSetting } from "../api/user";
import { ColumnVisibility } from "../components/Home/parts/projectListColumnWidths";

// SC_QUICK-2183 — プロジェクト一覧の並び替えと表示モードをユーザーごとに保存する。
// 保存済みの設定から sortBy/viewType を初期化する（未設定の場合、および取得・
// 保存に失敗した場合は create/grid にフォールバック — BR-4/AC-6/AC-9）。
// settingLoaded を公開し、保存済み設定が判明するまで一覧の描画を待たせることで、
// 初回描画時に誤った状態が一瞬見えるのを防ぐ（AC-10）。
// URLは一切参照しないため、共有リンクに並び替え・表示モードのパラメータが
// 付いていてもユーザー自身の保存済み設定が必ず優先される（AC-8）。

const DEFAULT_PROJECT_LIST_SORT_BY = Common.DEFAULT_PROJECT_LIST_SORT_BY;
const DEFAULT_PROJECT_LIST_VIEW_TYPE = Common.DEFAULT_PROJECT_LIST_VIEW_TYPE;

export const ALL_COLUMNS_VISIBLE: ColumnVisibility = {
  showName: true,
  showJobsite: true,
  showPhotographer: true,
  showType: true,
  showCaptureDate: true,
  showUploadDate: true,
  showImages: true,
  showStatus: true,
};

export type UseProjectListSettingResult = {
  sortBy: Common.ProjectListSortBy;
  viewType: Common.ProjectListViewType;
  settingLoaded: boolean;
  changeSortBy: (value: Common.ProjectListSortBy) => void;
  changeViewType: (value: Common.ProjectListViewType) => void;
  visibleColumns: ColumnVisibility;
  setVisibleColumns: (columns: ColumnVisibility) => void;
};

export const useProjectListSetting = (): UseProjectListSettingResult => {
  const [sortBy, setSortBy] = useState<Common.ProjectListSortBy>(
    DEFAULT_PROJECT_LIST_SORT_BY
  );
  const [visibleColumns, setVisibleColumns] =
    useState<ColumnVisibility>(ALL_COLUMNS_VISIBLE);

  const [viewType, setViewType] = useState<Common.ProjectListViewType>(
    DEFAULT_PROJECT_LIST_VIEW_TYPE
  );
  const [settingLoaded, setSettingLoaded] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    userSettingAPIs
      .get()
      .then((setting) => {
        if (!isMountedRef.current) return;
        setSortBy(setting?.projectListSortBy ?? DEFAULT_PROJECT_LIST_SORT_BY);
        setViewType(
          setting?.projectListViewType ?? DEFAULT_PROJECT_LIST_VIEW_TYPE
        );
        if (!setting) return;
        const columns = setting.displayListColumnSetting;
        setVisibleColumns({
          showName: true,
          showJobsite: columns?.showJobsite ?? true,
          showPhotographer: columns?.showPhotographer ?? true,
          showType: columns?.showType ?? true,
          showCaptureDate: columns?.showCaptureDate ?? true,
          showUploadDate: columns?.showUploadDate ?? true,
          showImages: columns?.showImages ?? true,
          showStatus: columns?.showStatus ?? true,
        });
      })
      .catch(() => {
        // AC-9 — 設定の取得に失敗した場合は既定値のまま、エラー表示はしない
      })
      .finally(() => {
        if (isMountedRef.current) setSettingLoaded(true);
      });

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const changeSortBy = useCallback((value: Common.ProjectListSortBy) => {
    setSortBy(value);
    updateProjectListSetting({ projectListSortBy: value }).catch(() => {
      // AC-9 — 設定の保存に失敗しても、ユーザーが選んだ値はUIに残す。
      // エラー表示はしない
    });
  }, []);

  const changeViewType = useCallback((value: Common.ProjectListViewType) => {
    setViewType(value);
    updateProjectListSetting({ projectListViewType: value }).catch(() => {});
  }, []);

  return { sortBy, viewType, settingLoaded, changeSortBy, changeViewType, visibleColumns, setVisibleColumns };

};
